/* FFL CUSTOM — Upsell "Complete Your Project" modal controller
   Listens for ffl:added-to-cart custom event (dispatched after intercepting /cart/add.js)
   and opens the modal with product context, then lazy-loads upsell suggestions per tab.
*/
(function () {
  'use strict';

  const ALLOWED_TYPE_PATTERNS = [/hardwood/i, /vinyl/i, /lvp/i, /laminate/i, /wall[\s-]?panel/i];
  const TAG_FOR_TAB = {
    toppicks: 'upsell-toppick',
    trim: 'upsell-trim',
    underlay: 'upsell-underlay',
    adhesive: 'upsell-adhesive',
    tools: 'upsell-tools',
  };

  /* FFL CUSTOM (external audit item 16, 2026-09-21)
     The audit's complaint was that the modal offers generic accessories: a vinyl buyer was shown
     "adhesive", not vinyl adhesive. The panel now asks for the material-specific tag first
     (upsell-adhesive-vinyl) and only falls back to the generic tag when the catalogue has nothing
     tagged that way — and when it does fall back, it SAYS SO on the panel instead of passing
     generic suggestions off as matched ones. */
  const MATERIAL_PATTERNS = [
    [/wall[\s-]?panel/i, 'wall-paneling', 'wall paneling'],
    [/laminate/i, 'laminate', 'laminate'],
    [/vinyl|lvp|lvt|rigid\s*core|spc|wpc/i, 'vinyl', 'vinyl'],
    [/hardwood|engineered|solid/i, 'hardwood', 'hardwood'],
  ];
  function detectMaterial(text) {
    const t = String(text || '');
    for (const [re, slug, label] of MATERIAL_PATTERNS) if (re.test(t)) return { slug, label };
    return null;
  }
  const SESSION_DISMISS_KEY = 'ffl_upsell_dismiss_count';
  const MAX_DISMISS_PER_SESSION = 3;

  class FFLUpsellModal {
    constructor() {
      this.modal = document.getElementById('ffl-upsell-modal');
      if (!this.modal) return;
      this.titleEl = this.modal.querySelector('.ffl-upsell__title');
      this.mainProductEl = this.modal.querySelector('.ffl-upsell__main-product');
      this.tabs = {};
      this.panels = {};
      this.cache = {};
      this.mainProductData = null;

      this.modal.querySelectorAll('[data-tab]').forEach(t => {
        this.tabs[t.dataset.tab] = t;
        t.addEventListener('click', () => this.switchTab(t.dataset.tab));
      });
      this.modal.querySelectorAll('[data-panel]').forEach(p => {
        this.panels[p.dataset.panel] = p;
      });
      this.modal.querySelectorAll('[data-close]').forEach(el => {
        el.addEventListener('click', () => this.close());
      });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      document.addEventListener('ffl:added-to-cart', (e) => this.open(e.detail));

      // Monkey-patch fetch to intercept /cart/add.js calls (works regardless of theme JS)
      const origFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const response = await origFetch(...args);
        try {
          const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
          if (url.includes('/cart/add')) {
            const cloned = response.clone();
            cloned.json().then(data => {
              document.dispatchEvent(new CustomEvent('ffl:added-to-cart', { detail: data }));
            }).catch(() => {});
          }
        } catch (_) {}
        return response;
      };
    }

    isOpen() { return this.modal.getAttribute('data-open') === 'true'; }

    async open(payload) {
      if (!payload) return;
      // payload may be { items: [...] } from /cart/add.js or a single item shape
      const item = payload.items ? payload.items[0] : payload;
      if (!item) return;
      const type = (item.product_type || '').toLowerCase();
      if (!ALLOWED_TYPE_PATTERNS.some(re => re.test(type))) return;
      if (window.location.pathname === '/cart') return;
      const dismissCount = parseInt(sessionStorage.getItem(SESSION_DISMISS_KEY) || '0', 10);
      if (dismissCount >= MAX_DISMISS_PER_SESSION) return;

      this.mainProductData = item;
      /* material drives which accessory tag is asked for; read from the product type first and
         from the title only as a fallback, because the type is the curated field */
      this.material = detectMaterial(type) || detectMaterial(item.product_title || item.title);
      this.cache = {};   // suggestions are per-material, so a new product must not reuse them
      this.setTitle(type);
      this.mainProductEl.textContent = item.product_title || item.title || 'your selection';

      // Reset all tabs/panels to initial state
      Object.entries(this.tabs).forEach(([k, t]) => {
        const active = k === 'toppicks';
        t.classList.toggle('ffl-upsell__tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      Object.entries(this.panels).forEach(([k, p]) => {
        p.hidden = k !== 'toppicks';
        p.classList.toggle('ffl-upsell__panel--active', k === 'toppicks');
      });

      this.modal.setAttribute('data-open', 'true');
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      const closeBtn = this.modal.querySelector('.ffl-upsell__close');
      if (closeBtn) closeBtn.focus();

      await this.loadPanel('toppicks');
    }

    setTitle(type) {
      if (/hardwood/i.test(type)) this.titleEl.textContent = 'Complete Your Hardwood Project';
      else if (/vinyl|lvp/i.test(type)) this.titleEl.textContent = 'Complete Your Vinyl Project';
      else if (/laminate/i.test(type)) this.titleEl.textContent = 'Complete Your Laminate Project';
      else if (/wall[\s-]?panel/i.test(type)) this.titleEl.textContent = 'Complete Your Wall Paneling Project';
      else this.titleEl.textContent = 'Complete Your Project';
    }

    async switchTab(tabName) {
      Object.entries(this.tabs).forEach(([k, t]) => {
        const active = k === tabName;
        t.classList.toggle('ffl-upsell__tab--active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      Object.entries(this.panels).forEach(([k, p]) => {
        p.hidden = k !== tabName;
        p.classList.toggle('ffl-upsell__panel--active', k === tabName);
      });
      if (!this.cache[tabName]) await this.loadPanel(tabName);
    }

    async loadPanel(tabName) {
      const panel = this.panels[tabName];
      if (!panel) return;
      const tag = TAG_FOR_TAB[tabName];
      if (!tag) return;

      if (this.cache[tabName]) {
        panel.innerHTML = this.cache[tabName];
        this.attachCardListeners(panel);
        return;
      }

      panel.innerHTML = '<div class="ffl-upsell__loading">Loading…</div>';

      try {
        /* Ask for the material-specific tag first (upsell-adhesive-vinyl), then the generic one.
           Two requests only ever happen when the specific tag returns nothing, which is the case
           the customer needs telling about anyway. */
        const mat = this.material;
        let products = [];
        let matched = false;
        if (mat) {
          products = await this.fetchByTag(`${tag}-${mat.slug}`);
          matched = products.length > 0;
        }
        if (!products.length) products = await this.fetchByTag(tag);

        if (products.length === 0) {
          panel.innerHTML = `<p class="ffl-upsell__empty">We're stocking these soon — check back later.<br><a href="/collections/all">Browse all products →</a></p>`;
          return;
        }

        /* The audit asks that general recommendations be labelled as general. Saying nothing
           would present a bag of generic accessories as if it had been matched to the floor the
           customer just bought. */
        const note = mat && !matched
          ? `<p class="ffl-upsell__note" role="note">General recommendations — we don't yet stock accessories matched specifically to ${mat.label}.</p>`
          : (mat ? `<p class="ffl-upsell__note ffl-upsell__note--matched">Matched to your ${mat.label}.</p>` : '');

        const cardsHtml = products.map(p => this.renderCard(p)).join('');
        const html = `${note}<div class="ffl-upsell__grid">${cardsHtml}</div>`;
        this.cache[tabName] = html;
        panel.innerHTML = html;
        this.attachCardListeners(panel);
      } catch (err) {
        console.error('[FFL Upsell] load failed:', err);
        panel.innerHTML = `<p class="ffl-upsell__empty">Couldn't load suggestions right now.<br><a href="/collections/all">Browse all products →</a></p>`;
      }
    }

    async fetchByTag(tag) {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent('tag:' + tag)}&resources[type]=product&resources[limit]=6`);
      const data = await res.json();
      return (data && data.resources && data.resources.results && data.resources.results.products) || [];
    }

    /* FFL CUSTOM (external audit item 16, 2026-09-21)
       /search/suggest.json is inconsistent: sometimes `price` comes back already formatted
       ("$10.99") and sometimes as a bare number. Printing it raw is how prices ended up in the
       modal with no currency symbol. Anything already carrying a symbol is passed straight
       through — re-formatting it would produce "$$10.99" — and a bare number is rendered through
       the shop's own money_format so the modal matches every other price on the site. */
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '';
      const raw = String(value).trim();
      if (/[^\d.,\s]/.test(raw)) return raw;               // already formatted
      const amount = parseFloat(raw.replace(/,/g, ''));
      if (!isFinite(amount)) return raw;
      const tpl = (window.Shopify && window.Shopify.money_format) || '${{amount}}';
      const group = (s) => s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withDec = group(amount.toFixed(2));
      const noDec = group(Math.round(amount).toString());
      return tpl
        .replace(/\{\{\s*amount\s*\}\}/g, withDec)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
    }

    renderCard(p) {
      // /search/suggest.json shape:
      // { url, title, vendor, image, price (formatted string), price_min, price_max, available, variants? }
      const img = p.image || '';
      const title = (p.title || '').replace(/"/g, '&quot;');
      const vendor = p.vendor || '';
      const priceDisplay = this.formatMoney(p.price || p.price_min || "");
      const url = p.url || (p.handle ? `/products/${p.handle}` : '#');
      // The /search/suggest.json product shape doesn't always include variant ids; we link to the PDP
      // for those cases instead of attempting one-click add.
      return `
        <div class="ffl-upsell__card">
          <a class="ffl-upsell__card-link" href="${url}" target="_blank" rel="noopener">
            <img class="ffl-upsell__card-image" src="${img}" alt="${title}" loading="lazy" width="180" height="180">
            ${vendor ? `<p class="ffl-upsell__card-vendor">${vendor}</p>` : ''}
            <p class="ffl-upsell__card-title">${title}</p>
            <p class="ffl-upsell__card-price">${priceDisplay}</p>
          </a>
          <a class="ffl-upsell__card-add" href="${url}" target="_blank" rel="noopener">View Product</a>
        </div>
      `;
    }

    attachCardListeners(_panel) {
      // No-op for now — cards link to PDP. Future: enrich to include variant_id from
      // a richer endpoint (e.g. /products/<handle>.js) for direct 1-click add.
    }

    close() {
      this.modal.removeAttribute('data-open');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.style.display = 'none';
      document.body.style.overflow = '';
      const c = parseInt(sessionStorage.getItem(SESSION_DISMISS_KEY) || '0', 10);
      sessionStorage.setItem(SESSION_DISMISS_KEY, String(c + 1));
    }
  }

  function init() {
    if (window.FFLUpsellModal) return;
    window.FFLUpsellModal = new FFLUpsellModal();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
